<app-navbar></app-navbar>
<div (click)="containerClick()"
     id="container"
     #container
     class="exam-page"
     tabindex="0"
     autofocus
     (keydown.arrowRight)="nextQuestion($event)"
     (keydown.arrowLeft)="prevQuestion($event)">
  <div class="exam-layout">
    <div class="exam-main">
      <!-- Question panel - no flags, no tabs in Exam mode -->
      <div class="panel exam-question-panel">
        <app-question-pool-question-detail (buttonClicked)="handleButtonClick()"
          [questionId]="questionId"
          [examMode]="true"
          [examTimeLeft]="examTimeLeft"
          [shuffleQuestionAnswers]="userTestSettings?.shuffleQuestionAnswers ?? false"></app-question-pool-question-detail>
      </div>
    </div>

    <!-- RHS panel - Exam controls -->
    <aside class="panel exam-rhs-panel">
      <h4 class="exam-rhs-title">Exam</h4>

      <div class="exam-rhs-actions">
        <button (click)="cancelExam()" class="exam-btn exam-btn-cancel">Cancel</button>
        <button (click)="requestPause()" class="exam-btn exam-btn-pause"
          [disabled]="pauseUsed"
          [title]="pauseUsed ? 'Pause already used' : 'Pause exam (max 15 minutes, can be used only once)'">
          <i class="bi bi-pause-fill"></i> Pause
        </button>
        <button (click)="requestFinish()" class="exam-btn exam-btn-finish">Finish Exam</button>
      </div>

      <div class="exam-progress-section">
        <label class="exam-progress-label">Answer progress</label>
        <div class="exam-progress-bar-wrap">
          <div class="exam-progress-bar exam-progress-bar-answer" role="progressbar"
            [style.width.%]="answerProgress" [attr.aria-valuenow]="answerProgress" aria-valuemin="0" aria-valuemax="100"></div>
        </div>
        <span class="exam-progress-text">{{ answeredCount }}/{{ totalQuestions }}</span>
      </div>

      <div class="exam-progress-section">
        <label class="exam-progress-label">Time left</label>
        <div class="exam-progress-bar-wrap">
          <div class="exam-progress-bar exam-progress-bar-time" role="progressbar"
            [style.width.%]="timeProgress"
            [class.exam-time-low]="timeProgress <= 20"
            [class.exam-time-mid]="timeProgress > 20 && timeProgress < 50"
            [class.exam-time-ok]="timeProgress >= 50"
            [attr.aria-valuenow]="timeProgress" aria-valuemin="0" aria-valuemax="100"></div>
        </div>
        <span class="exam-time-display">{{ formatExamTimeLeft() }}</span>
      </div>

      <label class="exam-progress-label">Question list</label>
      <div class="exam-question-grid" [class.exam-question-grid-paginated]="usePaginatedGrid">
        @for (id of getVisibleQuestionIds(); track id) {
          <a class="exam-q-btn"
             [routerLink]="['/question-pool-exam/', id+'', '0']"
             [class.exam-q-active]="questionsMap[id]?.id === (questionId || 1)"
             [class.exam-q-unanswered]="!questionsMap[id]?.answered"
             [class.exam-q-answered]="questionsMap[id]?.answered === true">
            {{ getQuestionNumber(id) }}
          </a>
        }
      </div>

      @if (usePaginatedGrid) {
      <nav class="exam-pagination" aria-label="Question grid pagination">
        <button type="button" class="exam-pagination-btn" (click)="goToPage(1)" [disabled]="questionGridPage <= 1" title="First page">««</button>
        <button type="button" class="exam-pagination-btn" (click)="goToPage(questionGridPage - 1)" [disabled]="questionGridPage <= 1" title="Previous page">«</button>
        @for (p of getPageNumbers(); track p) {
          <button type="button" class="exam-pagination-btn" [class.exam-pagination-active]="p === questionGridPage" (click)="goToPage(p)">{{ p }}</button>
        }
        <button type="button" class="exam-pagination-btn" (click)="goToPage(questionGridPage + 1)" [disabled]="questionGridPage >= getTotalPages()" title="Next page">»</button>
        <button type="button" class="exam-pagination-btn" (click)="goToPage(getTotalPages())" [disabled]="questionGridPage >= getTotalPages()" title="Last page">»»</button>
      </nav>
      }

      <button (click)="showShortcuts()" class="exam-btn exam-btn-shortcuts" title="Keyboard shortcuts">
        <i class="bi bi-keyboard"></i> Shortcuts
      </button>
    </aside>
  </div>

  <!-- Mobile bottom test bar — hidden on desktop via CSS. Cancel + Finish stay
       always reachable; the counter button opens the full grid as a sheet. -->
  <nav class="mobile-test-bar" aria-label="Exam navigation">
    <button type="button" class="mobile-test-btn mobile-test-btn-cancel" (click)="requestCancelFromMobile()" aria-label="Cancel exam">
      <i class="bi bi-x-lg"></i>
    </button>
    <button type="button" class="mobile-test-btn mobile-test-btn-arrow" (click)="prevQuestion($event)" aria-label="Previous question">
      <i class="bi bi-chevron-left"></i>
    </button>
    <button type="button" class="mobile-test-btn mobile-test-btn-counter" (click)="toggleGridSheet()" [attr.aria-expanded]="gridSheetOpen" aria-haspopup="dialog">
      <span>Q {{ getQuestionNumber(questionId) }} / {{ getTotalQuestions() }}</span>
      <i class="bi bi-grid-3x3-gap"></i>
    </button>
    <button type="button" class="mobile-test-btn mobile-test-btn-arrow" (click)="nextQuestion($event)" aria-label="Next question">
      <i class="bi bi-chevron-right"></i>
    </button>
    <button type="button" class="mobile-test-btn mobile-test-btn-finish" (click)="requestFinish()" aria-label="Finish exam">
      <i class="bi bi-check2-circle"></i>
    </button>
  </nav>

  @if (gridSheetOpen) {
    <div class="grid-sheet-backdrop" (click)="closeGridSheet()" (keydown)="$event.key === 'Escape' && closeGridSheet()" role="button" tabindex="0" aria-label="Close question list"></div>
    <div class="grid-sheet" role="dialog" aria-label="Question list">
      <div class="grid-sheet-header">
        <h3 class="grid-sheet-title">Questions</h3>
        <button type="button" class="grid-sheet-close" (click)="closeGridSheet()" aria-label="Close">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="grid-sheet-body">
        <div class="exam-question-grid" [class.exam-question-grid-paginated]="usePaginatedGrid">
          @for (id of getVisibleQuestionIds(); track id) {
            <a class="exam-q-btn"
               (click)="closeGridSheet()"
               [routerLink]="['/question-pool-exam/', id+'', '0']"
               [class.exam-q-active]="questionsMap[id]?.id === (questionId || 1)"
               [class.exam-q-unanswered]="!questionsMap[id]?.answered"
               [class.exam-q-answered]="questionsMap[id]?.answered === true">
              {{ getQuestionNumber(id) }}
            </a>
          }
        </div>
        @if (usePaginatedGrid) {
          <nav class="exam-pagination" aria-label="Question grid pagination">
            <button type="button" class="exam-pagination-btn" (click)="goToPage(1)" [disabled]="questionGridPage <= 1" title="First page">««</button>
            <button type="button" class="exam-pagination-btn" (click)="goToPage(questionGridPage - 1)" [disabled]="questionGridPage <= 1" title="Previous page">«</button>
            @for (p of getPageNumbers(); track p) {
              <button type="button" class="exam-pagination-btn" [class.exam-pagination-active]="p === questionGridPage" (click)="goToPage(p)">{{ p }}</button>
            }
            <button type="button" class="exam-pagination-btn" (click)="goToPage(questionGridPage + 1)" [disabled]="questionGridPage >= getTotalPages()" title="Next page">»</button>
            <button type="button" class="exam-pagination-btn" (click)="goToPage(getTotalPages())" [disabled]="questionGridPage >= getTotalPages()" title="Last page">»»</button>
          </nav>
        }
      </div>
    </div>
  }
</div>

<!-- Cancel Confirmation Modal -->
<div *ngIf="showCancelConfirm" class="modal-overlay" (click)="continueExam()">
  <div class="modal-dialog" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Cancel Exam?</h5>
        <button type="button" class="btn-close" (click)="continueExam()" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        @if (hasAnsweredQuestions()) {
          <p>You have answered some questions. What would you like to do?</p>
        } @else {
          <p>Cancel this exam and return to the question pool?</p>
        }
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-danger" (click)="cancelWithoutSaving()">Cancel without Saving</button>
        @if (hasAnsweredQuestions()) {
          <button type="button" class="btn btn-outline-warning" (click)="cancelToResumeLater()">Cancel to Resume Later</button>
        }
        <button type="button" class="btn btn-primary" (click)="continueExam()">Continue</button>
      </div>
    </div>
  </div>
</div>

<!-- Finish Confirmation Modal — only shown when the exam has unanswered
     questions. Fully-answered exams finish silently. -->
<div *ngIf="showFinishConfirm" class="modal-overlay" (click)="cancelFinish()">
  <div class="modal-dialog" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Finish Exam?</h5>
        <button type="button" class="btn-close" (click)="cancelFinish()" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p class="mb-0">
          You have <strong>{{ finishUnansweredCount }}</strong>
          @if (finishUnansweredCount === 1) { unanswered question. } @else { unanswered questions. }
          Submit now and stop the timer, or go back and answer the rest?
        </p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-success" (click)="confirmFinish()">
          <i class="bi bi-check2-circle"></i> Finish Exam
        </button>
        <button type="button" class="btn btn-primary" (click)="cancelFinish()">Continue Exam</button>
      </div>
    </div>
  </div>
</div>

<!-- Leave page confirmation (navigation away, back, link) -->
<div *ngIf="showLeaveConfirm" class="modal-overlay" (click)="confirmLeavePage(false)">
  <div class="modal-dialog" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Leave exam?</h5>
        <button type="button" class="btn-close" (click)="confirmLeavePage(false)" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p class="mb-0">Are you sure you want to leave without saving? You can keep your current state using the Cancel button.</p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary" #leaveModalStayBtn (click)="confirmLeavePage(false)">Stay</button>
        <button type="button" class="btn btn-outline-secondary" (click)="confirmLeavePage(true)">Leave</button>
      </div>
    </div>
  </div>
</div>

<!-- Pause Confirmation Modal -->
<div *ngIf="showPauseConfirm" class="modal-overlay" (click)="cancelPause()">
  <div class="modal-dialog" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Pause Exam?</h5>
        <button type="button" class="btn-close" (click)="cancelPause()" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p>You can pause the exam for a maximum of 15 minutes. The pause can be used only once.</p>
        <p class="mb-0">Are you sure you want to pause the exam?</p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="cancelPause()">Cancel</button>
        <button type="button" class="btn btn-primary" (click)="confirmPause()">Pause Exam</button>
      </div>
    </div>
  </div>
</div>

<!-- Pause Countdown Modal -->
<div *ngIf="isPaused" class="modal-overlay">
  <div class="modal-dialog modal-dialog-centered" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Exam Paused</h5>
      </div>
      <div class="modal-body text-center">
        <p>The exam is currently paused. You have a maximum of 15 minutes for the pause.</p>
        <p class="mb-3">The exam will automatically continue when the pause time expires.</p>
        <div class="pause-countdown">
          <h3 class="mb-0">{{ formatPauseTime(pauseTimeLeft) }}</h3>
          <small class="text-muted">Time remaining</small>
        </div>
      </div>
      <div class="modal-footer justify-content-center">
        <button type="button" class="btn btn-primary" (click)="resumeExam()">Continue Exam</button>
      </div>
    </div>
  </div>
</div>

<!-- Shortcuts Modal -->
<div *ngIf="showShortcutsModal" class="modal-overlay" (click)="closeShortcuts()">
  <div class="modal-dialog" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Keyboard Shortcuts</h5>
        <button type="button" class="btn-close" (click)="closeShortcuts()" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p><strong>Navigation:</strong></p>
        <ul>
          <li><kbd>←</kbd> (Left Arrow) - Previous question</li>
          <li><kbd>→</kbd> (Right Arrow) - Next question</li>
        </ul>
        <p><strong>Mark Answer:</strong></p>
        <ul>
          <li><kbd>1</kbd> - Select 1st answer (top)</li>
          <li><kbd>2</kbd> - Select 2nd answer</li>
          <li><kbd>3</kbd> - Select 3rd answer</li>
          <li><kbd>4</kbd> - Select 4th answer (bottom)</li>
        </ul>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary" (click)="closeShortcuts()">Close</button>
      </div>
    </div>
  </div>
</div>
